import React from 'react';
import { Input, Select, Typography } from 'antd';

import {
  SyntheticBondChoice,
  SyntheticBondFamilyMetadata,
  SyntheticBondGenerationOptions,
} from 'state';
import { InfoLabel } from '../BondsPlayground/ModelGuide';

type Props = {
  disabled?: boolean;
  metadata: SyntheticBondFamilyMetadata;
  options?: SyntheticBondGenerationOptions;
  onChange: (
    field: keyof SyntheticBondFamilyMetadata,
    value: string | number | null,
  ) => void;
};

const choiceValue = (choice: SyntheticBondChoice) => choice.value ?? choice.id;

const choiceLabel = (choice: SyntheticBondChoice) =>
  choice.label ||
  choice.name ||
  choice.geography ||
  String(choiceValue(choice));

const TEXT_FIELDS: Array<{
  field: keyof SyntheticBondFamilyMetadata;
  label: string;
  placeholder?: string;
}> = [
  { field: 'geography', label: 'Geography' },
  { field: 'country_code', label: 'Country code', placeholder: 'e.g. US' },
  { field: 'domicile', label: 'Domicile' },
  { field: 'asset_sector', label: 'Asset sector' },
];

const FamilyMetadataForm: React.FC<Props> = ({
  disabled,
  metadata,
  onChange,
  options,
}) => {
  const schema = options?.family_metadata_schema;
  const required = new Set(schema?.required_fields || []);
  const regions = schema?.region_choices || [];
  const marketHours = schema?.market_hours || [];

  return (
    <section className="synthetic-bonds__metadata">
      <div className="synthetic-bonds__section-title">
        <div>
          <Typography.Title level={5}>Family metadata</Typography.Title>
          <Typography.Paragraph type="secondary">
            These reviewed values are maintained by the Series and copied to
            every issue. Generated bonds are always sovereign, so issuer type is
            supplied by the server.
          </Typography.Paragraph>
        </div>
      </div>

      <div className="synthetic-bonds__metadata-grid">
        <label className="synthetic-bonds__field">
          <Typography.Text strong>
            <InfoLabel label="Region" title="Uniform family region">
              The PM region foreign key applied to every generated asset.
            </InfoLabel>
            {required.has('region') ? ' *' : ''}
          </Typography.Text>
          <Select
            allowClear
            disabled={disabled}
            options={regions.map(choice => ({
              value: choiceValue(choice),
              label: choiceLabel(choice),
            }))}
            placeholder="Select region"
            value={metadata.region ?? undefined}
            onChange={value => onChange('region', value ?? null)}
          />
        </label>

        <label className="synthetic-bonds__field">
          <Typography.Text strong>
            <InfoLabel label="Market hours" title="Uniform market hours">
              The PM MarketHours foreign key applied to every generated asset.
            </InfoLabel>
            {required.has('market_hours_id') ? ' *' : ''}
          </Typography.Text>
          <Select
            allowClear
            disabled={disabled}
            options={marketHours.map(choice => ({
              value: choiceValue(choice),
              label: choiceLabel(choice),
            }))}
            placeholder="Select market hours"
            value={metadata.market_hours_id ?? undefined}
            onChange={value => onChange('market_hours_id', value ?? null)}
          />
        </label>

        {TEXT_FIELDS.map(({ field, label, placeholder }) => (
          <label className="synthetic-bonds__field" key={field}>
            <Typography.Text strong>
              {label}
              {required.has(field) ? ' *' : ''}
            </Typography.Text>
            <Input
              disabled={disabled}
              maxLength={schema?.field_max_lengths?.[field]}
              placeholder={placeholder || label}
              value={String(metadata[field] ?? '')}
              onChange={event => onChange(field, event.target.value)}
            />
          </label>
        ))}
      </div>
    </section>
  );
};

export default FamilyMetadataForm;
